import { usePrefs } from './prefs';

/**
 * Sound (R23): one precached sprite file played through Web Audio. The AudioContext is made on
 * the first tap (browsers block it before), and the iOS audio session stays 'ambient' so the
 * silent switch mutes us. The sprite is built by scripts/make-sounds.mjs from plain math (no
 * samples, about 120 KB) and listed in client/public/sounds/tts-sprite.json, which a test
 * compares with the table below.
 *
 *   play('made')      a make: net and two dings          (alias of 'swish')
 *   play('miss')      a flat bonk, also good for undo     (alias of 'clank')
 *   play('buzzer')    the final horn
 *   play('tick')      a tap
 *   play('cheer')     a crowd and a fanfare
 *   play('whoosh')    a tier up
 */

export const SPRITE_URL = '/sounds/tts-sprite.wav';

/** [start s, duration s] inside the sprite. Keep in step with scripts/make-sounds.mjs. */
export const SPRITE = {
  tick: [0, 0.06],
  swish: [0.11, 0.35],
  clank: [0.51, 0.25],
  buzzer: [0.81, 0.6],
  whoosh: [1.46, 0.4],
  cheer: [1.91, 0.85],
} as const satisfies Record<string, readonly [number, number]>;

/** Friendly names that point at a clip, so callers can say what happened. */
export const SOUND_ALIASES = { made: 'swish', miss: 'clank' } as const satisfies Record<string, keyof typeof SPRITE>;

export type ClipName = keyof typeof SPRITE;
export type SoundName = ClipName | keyof typeof SOUND_ALIASES;

export function clipFor(name: SoundName): readonly [number, number] {
  const real: ClipName = name in SOUND_ALIASES ? SOUND_ALIASES[name as keyof typeof SOUND_ALIASES] : (name as ClipName);
  return SPRITE[real];
}

let ctx: AudioContext | null = null;
let buffer: AudioBuffer | null = null;
let loading: Promise<AudioBuffer | null> | null = null;

function audioContext(): AudioContext | null {
  if (ctx) return ctx;
  const Ctor =
    typeof window === 'undefined'
      ? undefined
      : (window.AudioContext ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
  if (!Ctor) return null;
  try {
    const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
    if (session) session.type = 'ambient';
  } catch {
    // older iOS: no audioSession, the default is ambient anyway
  }
  try {
    ctx = new Ctor();
  } catch {
    return null;
  }
  return ctx;
}

async function loadSprite(): Promise<AudioBuffer | null> {
  if (buffer) return buffer;
  if (!loading) {
    loading = (async () => {
      const c = audioContext();
      if (!c) return null;
      try {
        const res = await fetch(SPRITE_URL);
        if (!res.ok) throw new Error(`sprite answered ${res.status}`);
        buffer = await c.decodeAudioData(await res.arrayBuffer());
        return buffer;
      } catch {
        // Offline before the precache landed, or a decoder hiccup: try again on the next call.
        loading = null;
        return null;
      }
    })();
  }
  return loading;
}

/** Call from a tap. Safe to call many times. Resumes a suspended context and starts the sprite download. */
export function unlockAudio(): void {
  const c = audioContext();
  if (c?.state === 'suspended') void c.resume().catch(() => {});
  void loadSprite();
}

/** True once the context exists and is running (the browser let us make noise). */
export function audioReady(): boolean {
  return ctx?.state === 'running' && buffer !== null;
}

export async function play(name: SoundName, volume = 0.6): Promise<void> {
  if (!usePrefs.getState().sound) return;
  const c = audioContext();
  const buf = await loadSprite();
  if (!c || !buf) return;
  if (c.state === 'suspended') void c.resume().catch(() => {});
  const [start, dur] = clipFor(name);
  const src = c.createBufferSource();
  const gain = c.createGain();
  gain.gain.value = Math.max(0, Math.min(1, volume));
  src.buffer = buf;
  src.connect(gain).connect(c.destination);
  src.start(0, start, dur);
}

const UNLOCK_EVENTS = ['pointerup', 'click', 'keydown'] as const;

/**
 * Wire the one-time unlock on the first tap anywhere. iOS only counts a finished tap
 * (pointerup, click) as a user gesture for audio, so the listeners stay until the context runs.
 */
export function installAudioUnlock(): () => void {
  const off = () => {
    for (const e of UNLOCK_EVENTS) window.removeEventListener(e, handler);
  };
  const handler = () => {
    unlockAudio();
    if (ctx && ctx.state !== 'suspended') off();
  };
  for (const e of UNLOCK_EVENTS) window.addEventListener(e, handler, { passive: true });
  return off;
}

/** Test hook: forget the context and the decoded sprite. */
export function resetSoundForTests(): void {
  ctx = null;
  buffer = null;
  loading = null;
}
